import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, Edit2, Trash2, Calendar, Clock,
  FileText, AlertTriangle, Package, Pause, Play,
  Camera, Truck, CheckCircle, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const entryTypeConfig = {
  nota: { label: 'Nota', icon: FileText, color: 'bg-gray-100 text-gray-700 border-gray-300' },
  avanzamento: { label: 'Avanzamento', icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-300' },
  problema: { label: 'Problema', icon: AlertTriangle, color: 'bg-red-100 text-red-700 border-red-300' },
  materiali: { label: 'Materiali', icon: Package, color: 'bg-blue-100 text-blue-700 border-blue-300' },
  sospensione: { label: 'Sospensione', icon: Pause, color: 'bg-amber-100 text-amber-700 border-amber-300' },
  ripresa: { label: 'Ripresa', icon: Play, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  foto: { label: 'Foto', icon: Camera, color: 'bg-purple-100 text-purple-700 border-purple-300' },
  ordine: { label: 'Ordine', icon: Truck, color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  consegna: { label: 'Consegna', icon: Truck, color: 'bg-teal-100 text-teal-700 border-teal-300' }
};

// Mappa event_type automatici per filtri
const eventTypeCategories = {
  status_change: 'stato',
  building_practice: 'pratica',
  building_practice_validated: 'pratica',
  milestone_unlock: 'pagamenti',
  milestone_invoiced: 'pagamenti',
  milestone_paid: 'pagamenti',
  sal_update: 'sal',
  extra_created: 'extra',
  extra_updated: 'extra',
  document_uploaded: 'documenti'
};

const SiteLogPanel = ({ projectId, isReadOnly = false }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState('tutti'); // tutti, automatici, manuali, stato, sal, pagamenti, pratica, extra, documenti
  
  const [form, setForm] = useState({
    entry_type: 'nota',
    date: new Date().toISOString().split('T')[0],
    time: '',
    title: '',
    description: '',
    photos: []
  });

  useEffect(() => {
    if (projectId) {
      loadEntries();
    }
  }, [projectId]);

  const loadEntries = async () => {
    try {
      const response = await fetch(
        `/api/projects/${projectId}/site-log`,
        {
          withCredentials: true
        }
      );
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error loading site log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setEditingEntry(null);
    setForm({
      entry_type: 'nota',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      title: '',
      description: '',
      photos: []
    });
    setShowDialog(true);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      entry_type: entry.entry_type,
      date: entry.date,
      time: entry.time || '',
      title: entry.title,
      description: entry.description || '',
      photos: entry.photos || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Inserisci un titolo');
      return;
    }

    setSaving(true);
    try {
      const url = editingEntry
        ? `/api/projects/${projectId}/site-log/${editingEntry.id}`
        : `/api/projects/${projectId}/site-log`;
      
      const response = await fetch(url, {
        method: editingEntry ? 'PUT' : 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        throw new Error('Errore nel salvataggio');
      }

      toast.success(editingEntry ? 'Voce aggiornata' : 'Voce aggiunta al diario');
      setShowDialog(false);
      loadEntries();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Eliminare questa voce dal diario?')) return;

    try {
      const response = await fetch(
        `/api/projects/${projectId}/site-log/${entryId}`,
        {
          method: 'DELETE',
          withCredentials: true
        }
      );

      if (response.ok) {
        toast.success('Voce eliminata');
        loadEntries();
      } else {
        throw new Error('Errore');
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Raggruppa entries per data con useMemo per evitare ricalcoli
  // Applica filtro prima del raggruppamento
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      if (filter === 'tutti') return true;
      if (filter === 'automatici') return entry.is_system_generated === true;
      if (filter === 'manuali') return entry.is_system_generated !== true;
      
      // Filtri per categoria
      const eventCategory = eventTypeCategories[entry.event_type];
      if (filter === 'stato') return eventCategory === 'stato';
      if (filter === 'sal') return eventCategory === 'sal' || entry.entry_type === 'avanzamento';
      if (filter === 'pagamenti') return eventCategory === 'pagamenti';
      if (filter === 'pratica') return eventCategory === 'pratica';
      if (filter === 'extra') return eventCategory === 'extra';
      if (filter === 'documenti') return eventCategory === 'documenti';
      
      return true;
    });
  }, [entries, filter]);
  
  const groupedEntries = useMemo(() => {
    return filteredEntries.reduce((groups, entry) => {
      const date = entry.date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
      return groups;
    }, {});
  }, [filteredEntries]);

  const sortedDates = useMemo(
    () => Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a)),
    [groupedEntries]
  );
  
  // Conta automatici vs manuali per badge
  const autoCount = entries.filter(e => e.is_system_generated === true).length;
  const manualCount = entries.filter(e => e.is_system_generated !== true).length;

  return (
    <Card className="card-abitalo" data-testid="site-log-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle 
            className="text-lg flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <BookOpen className="w-5 h-5 text-[#FF7819]" />
            Diario di Cantiere
            <Badge variant="secondary" className="ml-2">{entries.length}</Badge>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </CardTitle>
          {!isReadOnly && (
            <Button size="sm" onClick={handleOpenNew} data-testid="add-site-log-entry">
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </Button>
          )}
        </div>
        
        {/* Filter bar unica */}
        {expanded && entries.length > 0 && (() => {
          const FILTERS = [
            { key: 'tutti', label: 'Tutti', count: entries.length },
            { key: 'automatici', label: 'Sistema', count: autoCount },
            { key: 'manuali', label: 'Manuali', count: manualCount },
            { key: 'stato', label: 'Stato' },
            { key: 'sal', label: 'SAL' },
            { key: 'pagamenti', label: 'Pagamenti' },
            { key: 'pratica', label: 'Pratica' },
            { key: 'extra', label: 'Extra' },
          ];
          return (
            <div className="mt-3 flex items-center gap-1 overflow-x-auto flex-nowrap rounded-lg bg-[#F5F5F5] p-1" data-testid="sitelog-filter-bar">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`sitelog-filter-${f.key}`}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 h-7 text-xs font-medium transition-colors ${active ? 'bg-[#FF7819] text-white shadow-sm' : 'text-[#525252] hover:bg-white/70'}`}
                  >
                    {f.label}
                    {f.count > 0 && (
                      <span className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full ${active ? 'bg-white/25 text-white' : 'bg-[#E4E4E4] text-[#525252]'}`}>{f.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}
      </CardHeader>
      
      {expanded && (
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-[#737373]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Caricamento...
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-[#737373]">
                {filter !== 'tutti' ? `Nessuna voce per filtro "${filter}"` : 'Nessuna voce nel diario'}
              </p>
              {!isReadOnly && filter === 'tutti' && (
                <p className="text-sm text-[#999]">Clicca "Aggiungi" per registrare la prima nota</p>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map((date) => (
                <div key={date}>
                  {/* Date header */}
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#FF7819]" />
                    <span className="font-medium text-[#3C3C3C]">{formatDate(date)}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  
                  {/* Entries for this date */}
                  <div className="space-y-2 pl-6 border-l-2 border-gray-200">
                    {groupedEntries[date]
                      .sort((a, b) => (b.time || '').localeCompare(a.time || ''))
                      .map((entry) => {
                        const config = entryTypeConfig[entry.entry_type] || entryTypeConfig.nota;
                        const Icon = config.icon;
                        
                        return (
                          <div 
                            key={entry.id}
                            className="relative pl-4 pb-3 group"
                          >
                            {/* Timeline dot */}
                            <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 bg-white ${config.color.includes('green') ? 'border-green-400' : config.color.includes('red') ? 'border-red-400' : config.color.includes('amber') ? 'border-amber-400' : config.color.includes('blue') ? 'border-blue-400' : 'border-gray-400'}`} />
                            
                            <div className="bg-[#FAFAFA] rounded-lg p-3 hover:bg-[#F2F2F2] transition-colors">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <Badge className={`text-xs ${config.color}`}>
                                      <Icon className="w-3 h-3 mr-1" />
                                      {config.label}
                                    </Badge>
                                    {/* Badge Sistema/Manuale */}
                                    {entry.is_system_generated ? (
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                        Sistema
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200">
                                        Manuale
                                      </Badge>
                                    )}
                                    {entry.time && (
                                      <span className="text-xs text-[#999] flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {entry.time}
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-medium text-[#3C3C3C]">{entry.title}</p>
                                  {entry.description && (
                                    <p className="text-sm text-[#737373] mt-1 whitespace-pre-wrap">{entry.description}</p>
                                  )}
                                  {entry.photos && entry.photos.length > 0 && (
                                    <div className="flex gap-2 mt-2 flex-wrap">
                                      {entry.photos.map((photo, idx) => (
                                        <a 
                                          key={`${entry.id}-photo-${idx}`}
                                          href={photo}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block w-16 h-16 rounded-lg bg-gray-200 overflow-hidden hover:opacity-80"
                                        >
                                          <img src={photo} alt="" className="w-full h-full object-cover" />
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-xs text-[#999] mt-2">
                                    {entry.created_by_name}
                                    {entry.user_role && entry.user_role !== 'system' && (
                                      <span className="ml-1 text-[#aaa]">({entry.user_role})</span>
                                    )}
                                  </p>
                                </div>
                                
                                {!isReadOnly && (
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => handleEdit(entry)}
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDelete(entry.id)}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* Dialog Aggiungi/Modifica */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#FF7819]" />
              {editingEntry ? 'Modifica Voce' : 'Nuova Voce Diario'}
            </DialogTitle>
            <DialogDescription>
              Registra un evento o una nota nel diario di cantiere
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Tipo */}
            <div className="grid gap-2">
              <Label>Tipo *</Label>
              <Select 
                value={form.entry_type} 
                onValueChange={(v) => setForm({...form, entry_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(entryTypeConfig).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {cfg.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Data e Ora */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({...form, date: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label>Ora</Label>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({...form, time: e.target.value})}
                />
              </div>
            </div>

            {/* Titolo */}
            <div className="grid gap-2">
              <Label>Titolo *</Label>
              <Input
                placeholder="Es: Completata posa pavimenti piano terra"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
              />
            </div>

            {/* Descrizione */}
            <div className="grid gap-2">
              <Label>Descrizione</Label>
              <Textarea
                placeholder="Dettagli aggiuntivi..."
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {editingEntry ? 'Salva Modifiche' : 'Aggiungi al Diario'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SiteLogPanel;
